import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getAccounts } from 'entities/cex-account';
import { getCexActualBalance } from 'entities/cex-statistic';
import { useTypedSelector } from 'store';
import { IAccountBalances, ICexAccount } from 'types/accounts';
import { ICexPair } from 'types/pairs';

const EMPTY_BALANCES: IAccountBalances = {
  base_balance: '0',
  base_balance_locked: '0',
  quote_balance: '0',
  quote_balance_locked: '0',
};

export interface AccountWithBalance extends ICexAccount, Partial<IAccountBalances> {}

type UseCexAccountsWithBalancesParams = {
  cexPair?: ICexPair;
  enabled?: boolean;
};

export const useCexAccountsWithBalances = ({
  cexPair,
  enabled = true,
}: UseCexAccountsWithBalancesParams) => {
  const projects = useTypedSelector(store => store.projects.projects);
  const archivedProjects = useTypedSelector(store => store.projects.archivedProjects);
  const allProjects = useMemo(
    () => [...(projects ?? []), ...(archivedProjects ?? [])],
    [projects, archivedProjects],
  );

  const cexPairIds = useMemo(() => {
    if (allProjects.length === 0 || !cexPair?.cex) return [];

    const ids = allProjects.flatMap(project =>
      (project.cexPairs ?? []).filter(pair => pair.cex === cexPair.cex).map(pair => pair.id),
    );
    return Array.from(new Set(ids)).sort((a, b) => a - b);
  }, [allProjects, cexPair?.cex]);

  const projectsLoaded = projects !== null && archivedProjects !== null;
  const query = useQuery({
    queryKey: ['pair-settings', 'cex-accounts-with-balances', cexPair?.id ?? null, cexPairIds],
    enabled: enabled && Boolean(cexPair) && cexPairIds.length > 0,
    staleTime: 60000,
    queryFn: async ({ signal }) => {
      if (!cexPair || cexPairIds.length === 0) {
        return [] as AccountWithBalance[];
      }

      const accountsByPair = await Promise.all(
        cexPairIds.map(async pairId => {
          const response = await getAccounts({ pairId, limit: 1000 }, signal);
          return response.data?.items ?? [];
        }),
      );

      const uniqueAccounts = new Map<number, ICexAccount>();
      for (const accounts of accountsByPair) {
        for (const account of accounts) {
          if (!uniqueAccounts.has(account.id)) {
            uniqueAccounts.set(account.id, account);
          }
        }
      }

      const symbol = `${cexPair.token_base.symbol}-${cexPair.token_quote.symbol}`;

      const withBalances = await Promise.all(
        uniqueAccounts.values().map(async account => {
          try {
            const response = await getCexActualBalance(
              { symbol, cex: cexPair.cex, account_id: [account.id] },
              signal,
            );

            return {
              ...account,
              ...EMPTY_BALANCES,
              ...(response.data ?? EMPTY_BALANCES),
            };
          } catch (error) {
            return {
              ...account,
              ...EMPTY_BALANCES,
            };
          }
        }),
      );

      return withBalances;
    },
  });

  return { ...query, projectsLoaded };
};
